'use client';

import { type ReactNode, useCallback, useEffect, useId, useMemo, useState } from 'react';
import { clsx } from 'clsx';
import { sortByKey } from '@interxfi/utilities/sort';
import { useIsWriteAllowed } from '../../access-context';
import DisclosureRightSmallIcon from '../../icons/react/icon-disclosure-right-small';
import { useIntersectionObserverAction } from '../../util/intersection-observer';
import { VerticalStack } from '../alignment/vertical-stack';
import { Button, type ButtonProps } from '../button/button';
import { useMergedDialogOpenProps } from '../dialog/utils';
import { Popover, type PopoverAlign, type PopoverSide } from '../popover/popover';
import { Typography } from '../typography';
import { type DropdownMenuItem, DropdownMenuListItem } from './list-item/dropdown-menu-list-item';
import { MultiGroupDropdownMenu } from './multi-group-dropdown-menu';
import styles from './dropdown-menu.module.scss';

interface DropdownMenuPagination {
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  fetchNextPage: () => Promise<any>;
}

function groupItems(items: DropdownMenuItem[]) {
  return sortByKey(items, (option) => option?.group || '').reduce(
    (grouped, item) => {
      const group = item.group || '';
      const items = grouped[group] || [];
      items.push(item);

      grouped[group] = items;

      return grouped;
    },
    {} as Record<string, DropdownMenuItem[]>,
  );
}

export type DropdownMenuGroupStyle = 'heading' | 'border';

export interface BaseDropdownMenuProps {
  align?: PopoverAlign;
  alignOffset?: number;
  className?: string;
  containerClassName?: string;
  ellipsizeOverflow?: boolean;
  forceShowGroupLabels?: boolean;
  groupStyle?: DropdownMenuGroupStyle;
  isOpen?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
  items: DropdownMenuItem[];
  label: string;
  labelIsHidden?: boolean;
  matchActivatorWidth?: boolean;
  pagination?: DropdownMenuPagination;
  popoverClassName?: string;
  side?: PopoverSide;
  sideOffset?: number;
}

export interface DropdownMenuPropsWithActivator extends BaseDropdownMenuProps {
  activator: ReactNode;
}

export interface DropdownMenuPropsWithoutActivator extends BaseDropdownMenuProps {
  activatorProps?: ButtonProps;
}

export type DropdownMenuProps = DropdownMenuPropsWithActivator | DropdownMenuPropsWithoutActivator;

export function DropdownMenu<T extends DropdownMenuProps>({
  align,
  alignOffset,
  className,
  containerClassName,
  ellipsizeOverflow,
  forceShowGroupLabels,
  groupStyle = 'heading',
  isOpen: providedIsOpen,
  onOpenChange: providedOnOpenChange,
  items,
  label,
  labelIsHidden,
  matchActivatorWidth,
  pagination,
  popoverClassName,
  side,
  sideOffset,
  ...rest
}: T) {
  const { activator } = rest as Partial<DropdownMenuPropsWithActivator>;
  const { activatorProps } = rest as Partial<DropdownMenuPropsWithoutActivator>;
  const id = useId();
  const menuId = `menu-${id}`;
  const labelId = `label-${id}`;
  const isWriteAllowed = useIsWriteAllowed();
  const visibleItems = useMemo(() => (isWriteAllowed ? items : items.filter((item) => !item.requiresWrite)), [items, isWriteAllowed]);
  const groupedItems = useMemo(() => groupItems(visibleItems), [visibleItems]);
  const hasMultipleGroups = Object.keys(groupedItems).length > 1;
  const [isOpen, onOpenChange] = useMergedDialogOpenProps({ isOpen: providedIsOpen, onOpenChange: providedOnOpenChange });
  const { className: buttonClassName, ...buttonProps } = activatorProps || {};
  const [openDialogs, setOpenDialogs] = useState(new Set<string>());
  const handleCloseMenu = useCallback(() => {
    onOpenChange(false);
  }, [onOpenChange]);
  const handleDialogOpen = useCallback((subItemId: string) => {
    setOpenDialogs((prev) => {
      if (!prev.has(subItemId)) {
        prev.add(subItemId);
        return new Set(prev);
      }

      return prev;
    });
  }, []);

  const handleDialogClose = useCallback((subItemId: string) => {
    setOpenDialogs((prev) => {
      if (!prev.has(subItemId)) {
        return prev;
      }

      prev.delete(subItemId);

      return new Set(prev);
    });
  }, []);

  useEffect(() => {
    onOpenChange(openDialogs.size > 0);
  }, [openDialogs.size, onOpenChange]);

  const { fetchNextPage, hasNextPage, isFetchingNextPage } = pagination || {};
  const [setObservedItemRef] = useIntersectionObserverAction(fetchNextPage, {
    disabled: !hasNextPage || isFetchingNextPage || !fetchNextPage,
  });

  // hide the menu entirely when access gating removes every item, but leave genuinely-empty menus to their call sites
  if (!visibleItems.length && items.length) {
    return null;
  }

  return (
    <div className={clsx(styles.dropdownMenu, className)}>
      <VerticalStack className={containerClassName} gap={1}>
        {!labelIsHidden && (
          <Typography className={styles.label} id={labelId} lineHeight="1.5rem" size="0.75rem" transform="uppercase" weight="bold">
            {label}
          </Typography>
        )}

        <Popover
          activator={
            activator || (
              <Button
                affixIcon={<DisclosureRightSmallIcon aria-hidden className={styles.disclosureIcon} />}
                affixIconPosition="right"
                className={clsx(styles.activator, buttonClassName)}
                contentClassName={styles.buttonContentContainer}
                title={typeof buttonProps.children === 'string' ? buttonProps.children : undefined}
                type="button"
                variant="light"
                {...buttonProps}
                aria-label={labelIsHidden ? label : undefined}
                aria-labelledby={labelIsHidden ? undefined : labelId}
              >
                {buttonProps.children ? <span className={styles.content}>{buttonProps.children}</span> : null}
              </Button>
            )
          }
          align={align}
          alignOffset={alignOffset}
          className={clsx(styles.popover, popoverClassName)}
          matchActivatorWidth={matchActivatorWidth}
          onOpen={onOpenChange}
          open={isOpen}
          side={side}
          sideOffset={sideOffset}
        >
          {hasMultipleGroups || forceShowGroupLabels ? (
            <MultiGroupDropdownMenu
              ellipsizeOverflow={ellipsizeOverflow}
              groupedItems={groupedItems}
              groupStyle={groupStyle}
              handleCloseMenu={handleCloseMenu}
              handleDialogClose={handleDialogClose}
              handleDialogOpen={handleDialogOpen}
              isOpen={isOpen}
              menuId={menuId}
              openDialogs={openDialogs}
              setObservedItemRef={setObservedItemRef}
            />
          ) : (
            <ul className={styles.menu} hidden={!isOpen || Boolean(openDialogs.size)} id={menuId}>
              {visibleItems.map((item, i, arr) => (
                <DropdownMenuListItem
                  ellipsizeOverflow={ellipsizeOverflow}
                  handleCloseDialog={handleDialogClose}
                  handleCloseMenu={handleCloseMenu}
                  handleOpenDialog={handleDialogOpen}
                  hasOpenDialog={openDialogs.has(item.id)}
                  key={item.id}
                  ref={i === arr.length - 1 ? setObservedItemRef : undefined}
                  {...item}
                />
              ))}
            </ul>
          )}
        </Popover>
      </VerticalStack>
    </div>
  );
}
